/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useId, makeStyles, mergeClasses, tokens, shorthands } from '@fluentui/react-components';


const thumbSizeVar = '--thumb-size';
const railSizeVar = '--rail-size';
const railColorVar = '--rail-color';
const progressColorVar = '--progress-color';
const thumbColorVar = '--thumb-color';

export const sliderCSSVars = {
  sliderMinProgressVar: '--slider-min-progress',
  sliderMaxProgressVar: '--slider-max-progress',
};

const {
  sliderMinProgressVar,
  sliderMaxProgressVar,
} = sliderCSSVars;

const useStyles = makeStyles({
  videoDuration: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'left',
  },
  videoDurationScrollbar: {
    position: 'relative',
    display: 'inline-grid',
    touchAction: 'none',
    alignItems: 'center',
    justifyItems: 'center',
    minWidth: '240px',
    // 3x3 grid with the rail and thumb in the center cell [2,2] and the hidden input stretching across all cells
    gridTemplateRows: `1fr var(${thumbSizeVar}) 1fr`,
    gridTemplateColumns: `1fr calc(100% - var(${thumbSizeVar})) 1fr`,
    [thumbSizeVar]: '20px',
    [railSizeVar]: '4px',
    minHeight: '32px',
    marginRight: '8px',
  },
  colors: {
    [railColorVar]: tokens.colorNeutralStrokeAccessible,
    [progressColorVar]: tokens.colorCompoundBrandBackground,
    [thumbColorVar]: tokens.colorCompoundBrandBackground,
    ':hover': {
      [thumbColorVar]: tokens.colorCompoundBrandBackgroundHover,
      [progressColorVar]: tokens.colorCompoundBrandBackgroundHover,
    },
    ':active': {
      [thumbColorVar]: tokens.colorCompoundBrandBackgroundPressed,
      [progressColorVar]: tokens.colorCompoundBrandBackgroundPressed,
    },
    '@media (forced-colors: active)': {
      [railColorVar]: 'CanvasText',
      [thumbColorVar]: 'Highlight',
      [progressColorVar]: 'Highlight',
      ':hover': {
        [thumbColorVar]: 'Highlight',
        [progressColorVar]: 'Highlight',
      },
    },
  },
  rail: {
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    gridRowStart: '2',
    gridRowEnd: '2',
    gridColumnStart: '2',
    gridColumnEnd: '2',
    position: 'relative',
    forcedColorAdjust: 'none',
    // Background gradient represents the progress of the slider
    backgroundImage: `linear-gradient(90deg,
      var(${railColorVar}) 0%,
      var(${railColorVar}) var(${sliderMinProgressVar}),
      var(${progressColorVar}) var(${sliderMinProgressVar}),
      var(${progressColorVar}) var(${sliderMaxProgressVar}),
      var(${railColorVar}) var(${sliderMaxProgressVar})
    )`,
    outlineWidth: '1px',
    outlineStyle: 'solid',
    outlineColor: tokens.colorTransparentStroke,
    width: '100%',
    height: `var(${railSizeVar})`,
  },
  thumb: {
    gridRowStart: '2',
    gridRowEnd: '2',
    gridColumnStart: '2',
    gridColumnEnd: '2',
    position: 'absolute',
    width: `var(${thumbSizeVar})`,
    height: `var(${thumbSizeVar})`,
    cursor: 'pointer',
    outlineStyle: 'none',
    forcedColorAdjust: 'none',
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    boxShadow: `0 0 0 calc(var(${thumbSizeVar}) * .2) ${tokens.colorNeutralBackground1} inset`,
    backgroundColor: `var(${thumbColorVar})`,
    '::before': {
      position: 'absolute',
      top: '0px',
      left: '0px',
      bottom: '0px',
      right: '0px',
      ...shorthands.borderRadius(tokens.borderRadiusCircular),
      boxSizing: 'border-box',
      content: "''",
      ...shorthands.border(`calc(var(${thumbSizeVar}) * .05)`, 'solid', tokens.colorNeutralStroke1),
    },
    transform: 'translateX(-50%)',
  },
  rangeInput: {
    gridRowStart: '2',
    gridRowEnd: '-2',
    gridColumnStart: '2',
    gridColumnEnd: '-2',
    pointerEvents: 'none',
    opacity: '0',
    ...shorthands.margin(0),
    ...shorthands.padding(0),
  },
  minRangeInput: {
    '&:focus ~ .video-duration-scrollbar__minThumb::after': {
      content: '""',
      position: 'absolute',
      pointerEvents: 'none',
      zIndex: 1,

      ...shorthands.border('2px', 'solid', tokens.colorStrokeFocus2),
      ...shorthands.borderRadius(tokens.borderRadiusMedium),

      top: '-4px',
      right: '-4px',
      bottom: '-4px',
      left: '-4px',
    },
  },
  maxRangeInput: {
    '&:focus ~ .video-duration-scrollbar__maxThumb::after': {
      content: '""',
      position: 'absolute',
      pointerEvents: 'none',
      zIndex: 1,

      ...shorthands.border('2px', 'solid', tokens.colorStrokeFocus2),
      ...shorthands.borderRadius(tokens.borderRadiusMedium),

      top: '-4px',
      right: '-4px',
      bottom: '-4px',
      left: '-4px',
    },
  },
  minThumb: {
    left: `var(${sliderMinProgressVar})`,
  },
  maxThumb: {
    left: `var(${sliderMaxProgressVar})`,
  },
});

const getPercent = (value, min, max) => (max === min ? 0 : ((value - min) / (max - min)) * 100);

const Slider = (props) => {
  const {
    setValue,
    getRelatedValue,
    className,
  } = props;

  const [isDragging, setIsDragging] = useState(false);

  const onMouseDown = (e) => {
    if (e.button !== 0) {
      return;
    }
    setIsDragging(true);
    const newValue = getRelatedValue(e);
    setValue(newValue);
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (isDragging) {
      const onMouseMove = (e) => {
        const newValue = getRelatedValue(e);
        setValue(newValue);
      };
      const onMouseUp = (e) => {
        setIsDragging(false);
        const newValue = getRelatedValue(e);
        setValue(newValue, true);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
    return () => {};
  }, [getRelatedValue, isDragging, setValue]);

  return (
    <div
      role="presentation"
      className={className}
      onMouseDown={(e) => {
        console.log(e);
        onMouseDown(e);
      }}
    />
  );
};

// input slider for video duration
// two thumbs for min and max duration
// one rail for the range
export const VideoDurationScrollbarFilter = (props) => {
  const minInputId = useId('min-input');
  const maxInputId = useId('max-input');
  const {
    min,
    max,
    unlimited,
    i18n,
    minDurationFilter,
    maxDurationFilter,
    onChange,
  } = props;

  const [minValue, setMinValue] = useState(minDurationFilter);
  const [maxValue, setMaxValue] = useState(maxDurationFilter);
  const classes = useStyles();
  const scrollbarRef = useRef();

  const styleVariables = {
    [sliderMinProgressVar]: (minValue == null ? '0%' : `${getPercent(minValue, min, unlimited)}%`),
    [sliderMaxProgressVar]: (maxValue == null ? '100%' : `${getPercent(maxValue, min, unlimited)}%`),
  };

  const wrappedSetMinValue = (value, shouldCallOnChange) => {
    const newValue = Math.min(Math.min(maxValue || unlimited, max), Math.max(min, value));
    setMinValue(newValue);
    if (shouldCallOnChange) {
      onChange({ newMinDuration: newValue, newMaxDuration: maxValue });
    }
  };

  const wrappedSetMaxValue = (value, shouldCallOnChange) => {
    const newValue = Math.min(unlimited, Math.max(minValue || 0, value));
    setMaxValue(newValue);
    if (shouldCallOnChange) {
      onChange({ newMinDuration: minValue, newMaxDuration: newValue });
    }
  };

  const getRelatedValue = ({
    clientX,
  }) => {
    if (!scrollbarRef.current) {
      return null;
    }
    const scrollbarBox = scrollbarRef.current.getBoundingClientRect();
    const {
      left,
      width,
    } = scrollbarBox;

    const x = clientX - left;
    return parseInt(Math.min(unlimited, Math.max(min, ((x / width) * (unlimited - min)) + min)), 10);
  };

  const onClickRail = (e) => {
    const value = getRelatedValue(e);
    if (value != null) {
      // set the min value if the click is closer to the min thumb
      // set the max value if the click is closer to the max thumb
      const minDistance = Math.abs((minValue || 0) - value);
      const maxDistance = Math.abs((maxValue || unlimited) - value);
      if (minDistance < maxDistance) {
        wrappedSetMinValue(value, true);
      } else {
        wrappedSetMaxValue(value, true);
      }
    }
  };


  return (
    <div className={mergeClasses('video-duration', classes.videoDuration)}>
      <div ref={scrollbarRef} className={mergeClasses('video-duration-scrollbar', classes.videoDurationScrollbar, classes.colors)} style={styleVariables}>
        <input
          type="range"
          min={min}
          max={unlimited}
          value={minValue}
          id={minInputId}
          className={mergeClasses(classes.rangeInput, classes.minRangeInput, 'min-value')}
          onChange={(e) => {
            wrappedSetMinValue(parseInt(e.target.value, 10), true);
          }}
          aria-label={i18n.getString(_TL_('Min value: {{ value }}'), { value: minValue })}
        />
        <input
          type="range"
          min={min}
          max={unlimited}
          value={maxValue}
          id={maxInputId}
          className={mergeClasses(classes.rangeInput, classes.maxRangeInput, 'max-value')}
          onChange={(e) => {
            wrappedSetMaxValue(parseInt(e.target.value, 10), true);
          }}
          aria-label={i18n.getString(_TL_('Max value: {{ value }}'), { value: maxValue > max ? 'unlimited' : maxValue })}
        />
        <div
          role="presentation"
          className={mergeClasses('video-duration-scrollbar__rail', classes.rail)}
          onClick={(e) => {
            onClickRail(e);
          }}
        />
        <Slider
          value={minValue}
          setValue={wrappedSetMinValue}
          getRelatedValue={getRelatedValue}
          className={mergeClasses('video-duration-scrollbar__minThumb', classes.minThumb, classes.thumb)}
        />
        <Slider
          value={maxValue}
          setValue={wrappedSetMaxValue}
          getRelatedValue={getRelatedValue}
          className={mergeClasses('video-duration-scrollbar__maxThumb', classes.maxThumb, classes.thumb)}
        />
      </div>
      <div className="range-text">
        {maxValue > max || maxValue == null ? `≥${i18n.getString(_TL_('{{ min }} seconds'), {
          min: minValue,
        })}` : i18n.getString(_TL_('{{ min }}-{{ max }} seconds'), {
          min: minValue,
          max: maxValue,
        })}
      </div>
    </div>
  );
};


VideoDurationScrollbarFilter.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  unlimited: PropTypes.number,
  i18n: PropTypes.shape({}).isRequired,
};

VideoDurationScrollbarFilter.defaultProps = {
  min: 0,
  max: 120,
  unlimited: 121,
};
